import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { GLOBE_RADIUS, latLonToVector3, magnitudeToSpread } from "../lib/seismic";
import { useSim } from "../lib/store";

const vertexShader = /* glsl */ `
  attribute float aDist;
  varying float vDist;
  void main() {
    vDist = aDist;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform float uFront;
  uniform float uOn;
  varying float vDist;
  void main() {
    float flood = 1.0 - smoothstep(uFront - 0.03, uFront + 0.03, vDist);
    float edge = exp(-pow((vDist - uFront) / 0.05, 2.0));
    vec3 col = mix(vec3(0.75, 0.06, 0.05), vec3(1.0, 0.38, 0.2), edge);
    float a = clamp(flood * 0.8 + edge * 1.0, 0.0, 1.0) * uOn;
    if (a < 0.01) discard;
    gl_FragColor = vec4(col, a);
  }
`;

export function CoastlineFlood({ event }) {
  const [data, setData] = useState(null);
  const matRef = useRef();

  useEffect(() => {
    let alive = true;
    fetch("/data/coastline.json")
      .then((r) => r.json())
      .then((geo) => {
        if (!alive) return;
        const positions = [];
        const units = [];
        for (const f of geo.features || []) {
          const g = f.geometry;
          if (!g) continue;
          const lines = g.type === "MultiLineString" ? g.coordinates : [g.coordinates];
          for (const line of lines) {
            for (let i = 0; i < line.length - 1; i++) {
              const a = latLonToVector3(line[i][1], line[i][0], GLOBE_RADIUS + 0.006);
              const b = latLonToVector3(line[i + 1][1], line[i + 1][0], GLOBE_RADIUS + 0.006);
              positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
              const ua = a.clone().normalize();
              const ub = b.clone().normalize();
              units.push(ua.x, ua.y, ua.z, ub.x, ub.y, ub.z);
            }
          }
        }
        const geom = new THREE.BufferGeometry();
        geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        const count = positions.length / 3;
        geom.setAttribute("aDist", new THREE.BufferAttribute(new Float32Array(count), 1));
        setData({ geom, units: new Float32Array(units), count });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!data || !event) return;
    const epi = latLonToVector3(event.lat, event.lon, 1).normalize();
    const attr = data.geom.getAttribute("aDist");
    const u = data.units;
    for (let i = 0; i < data.count; i++) {
      const dot = u[i * 3] * epi.x + u[i * 3 + 1] * epi.y + u[i * 3 + 2] * epi.z;
      attr.array[i] = Math.acos(THREE.MathUtils.clamp(dot, -1, 1));
    }
    attr.needsUpdate = true;
  }, [data, event]);

  const uniforms = useMemo(() => ({ uFront: { value: 0 }, uOn: { value: 0 } }), []);

  useFrame(() => {
    if (!matRef.current || !event) return;
    const st = useSim.getState();
    const elapsed = (performance.now() - st.simStart) / 1000;
    const on = st.simulating && event.offshore && st.tsunamiEnabled && elapsed <= event.duration + 3 ? 1 : 0;
    uniforms.uOn.value += (on - uniforms.uOn.value) * 0.08;
    if (on) {
      const smoothP = Math.min(elapsed / event.duration, 1);
      const spread = magnitudeToSpread(event.magnitude);
      uniforms.uFront.value = Math.min(Math.PI, spread * (0.12 + 0.95 * smoothP));
    }
  });

  if (!data || !event) return null;

  return (
    <lineSegments geometry={data.geom}>
      <shaderMaterial
        ref={matRef}
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </lineSegments>
  );
}
